import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Variable, Loader2, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import { WiredConexao, WiredFuncionalidade, wiredService } from "../../../services/wired";

interface WiredFuncionalidadeModalProps {
  isOpen: boolean;
  onClose: () => void;
  funcionalidade: WiredFuncionalidade | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

const SEM_CONEXAO = "none";

const validationSchema = Yup.object({
  nome_variavel: Yup.string().trim().max(100, "Nome da variável deve ter no máximo 100 caracteres"),
});

export function WiredFuncionalidadeModal({
  isOpen,
  onClose,
  funcionalidade,
  conexoes,
  onSuccess,
}: WiredFuncionalidadeModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState<string>(SEM_CONEXAO);
  const [nomeVariavel, setNomeVariavel] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (funcionalidade) {
      setIdConexao(funcionalidade.id_conexao || SEM_CONEXAO);
      setNomeVariavel(funcionalidade.nome_variavel || "");
      setAtivo(funcionalidade.ativo);
    }
    setError("");
  }, [funcionalidade, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!funcionalidade) return;

    try {
      validationSchema.validateSync({ nome_variavel: nomeVariavel });
    } catch (err: any) {
      setError(err.message);
      return;
    }

    try {
      setSubmitting(true);
      await wiredService.atualizarFuncionalidade(funcionalidade.id, {
        id_conexao: idConexao === SEM_CONEXAO ? null : idConexao,
        nome_variavel: nomeVariavel.trim(),
        ativo,
      });
      enqueueSnackbar("Funcionalidade atualizada com sucesso!", { variant: "success" });
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar funcionalidade", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !funcionalidade) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Variable className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Editar Funcionalidade
            </CardTitle>
            <CardDescription>
              {funcionalidade.descricao}{" "}
              <span className="font-mono text-xs">({funcionalidade.codigo})</span>
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
            disabled={submitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_CONEXAO}>Nenhuma</SelectItem>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome_variavel">Nome da variável</Label>
              <VariavelWiredSelect
                id="nome_variavel"
                value={nomeVariavel}
                onChange={(v) => {
                  setNomeVariavel(v);
                  setError("");
                }}
                idConexao={idConexao === SEM_CONEXAO ? "" : idConexao}
                ativo={isOpen}
                placeholder="Buscar variável (ex: patente)"
                disabled={submitting}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <p className="text-xs text-muted-foreground">
                Nome da variável WIRED de {funcionalidade.escopo || "usuário"} que receberá o valor no quarto.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="ativo-func">Ativo</Label>
                <p className="text-xs text-muted-foreground">Funcionalidades inativas não geram sincronizações.</p>
              </div>
              <Switch id="ativo-func" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Atualizando...
                  </>
                ) : (
                  "Atualizar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
